import React, { useEffect, useMemo } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import DesktopLayout from "@components-shared/desktop/layout/DesktopLayout";
import { Loader } from "@components/loader/desktop";
import { HeaderType } from "@data-types/layout/headerTypeEnum";
import { RegistrationFormData } from "@data-types/membership/registration";
import { ConfigType } from "@data-types/use-scroll-restoration/useScrollRestoration";
import OnboardingRegistrationError from "@features/membership/desktop/registration-types/onboarding-registration/OnboardingRegistrationError";
import OnboardingRegistrationSuccess from "@features/membership/desktop/registration-types/onboarding-registration/OnboardingRegistrationSuccess";
import OnboardingRegistrationTabs from "@features/membership/desktop/registration-types/onboarding-registration/OnboardingRegistrationTabs";
import OnboardingRegistrationTakeoverLimit from "@features/membership/desktop/registration-types/onboarding-registration/OnboardingRegistrationTakeoverLimit";
import {
	OnBoardingFooter,
	OnBoardingSlider,
} from "@features/membership/desktop/registration-types/shared";
import RestrictedSection from "@features/membership/desktop/registration-types/shared/RestrictedSection";
import { useRootAppStore } from "@hooks";
import { useScrollRestoration } from "@hooks/useScrollRestoration";
import {
	RegistrationStoreContext,
	useRegistrationStore,
} from "@state/context/membership/registration-types/RegistrationStoreContext";
import { RegistrationStore } from "@state/stores/membership/registration";
import { setTabTitle } from "@utils";
import { observer } from "mobx-react";

import OnboardingRegistrationAccountDetails from "./OnboardingRegistrationAccountDetails";
import OnboardingRegistrationContactDetails from "./OnboardingRegistrationContactDetails";

export default observer(function OnboardingRegistrationPage() {
	const registrationStore = useMemo(() => new RegistrationStore(), []);

	const { t } = useTranslation("registration");

	const rootStore = useRootAppStore();

	const { onInitialize, onDispose } = registrationStore;

	const url = new URL(window.location.href);

	useScrollRestoration({ type: ConfigType.Basic });

	useEffect(() => {
		setTabTitle(t("REGISTRATION_TITLE"));

		if (url.searchParams.has("reload")) {
			url.searchParams.delete("reload");
			window.history.pushState({}, "", url);
			window.location.reload();
			return;
		}

		onInitialize();

		return () => {
			onDispose();

			rootStore.userAuthStore?.userMatchingStore?.setIsRegistrationSuccess(
				false
			);
		};
	}, []);

	return (
		<RegistrationStoreContext.Provider value={registrationStore}>
			<RegistrationPageContent />
		</RegistrationStoreContext.Provider>
	);
});

const RegistrationPageContent = observer(function RegistrationPageContent() {
	const methods = useForm({
		mode: "onChange",
		defaultValues: {
			cultureInfoName: "en-US",
		},
	});
	const { handleSubmit } = methods;

	const { t } = useTranslation("registration");

	const rootStore = useRootAppStore();

	const {
		isRegistrationSuccessful,
		isLoading,
		currentStep,
		register,
		takeoverTempInfoId,
	} = useRegistrationStore();

	const params = new URLSearchParams(window.location.search);
	const affiliateCode = params.get("afc");

	if (
		isRegistrationSuccessful === true ||
		rootStore.userAuthStore?.userMatchingStore?.isRegistrationSuccess
	) {
		return <OnboardingRegistrationSuccess />;
	}

	if (isRegistrationSuccessful === false) {
		return <OnboardingRegistrationError />;
	}

	if (takeoverTempInfoId) {
		return <OnboardingRegistrationTakeoverLimit />;
	}

	return (
		<DesktopLayout
			headerType={HeaderType.EMPTY}
			mainClassName="at-OnboardingRegistrationPage--de l--membership"
		>
			<Loader isLoading={isLoading} />

			<section className="l--content l--content--full">
				<div className="l--content__main u-padd--reset">
					<div className="l--membership__container">
						<div className="card--membership card--primary">
							<OnBoardingSlider hasOnboardingStyling={true} />
							<FormProvider {...methods}>
								<form
									className="card--membership__main"
									onSubmit={handleSubmit(
										(formData: RegistrationFormData) =>
											register(formData, affiliateCode)
									)}
								>
									{rootStore.isIpRestricted ? (
										<RestrictedSection />
									) : null}
									<div className="card--membership__header">
										<div className="card--membership__title">
											{currentStep === 1
												? t("ACCOUNT_DETAILS_TAB")
												: t("CONTACT_DETAILS_TAB")}
										</div>
										<OnboardingRegistrationTabs />
									</div>

									<OnboardingSteps />
								</form>
							</FormProvider>
						</div>

						<OnBoardingFooter
							isRegistration
							hasOnboardingStyling={true}
						/>
					</div>
				</div>
			</section>
		</DesktopLayout>
	);
});

const OnboardingSteps = observer(function OnboardingSteps() {
	const { currentStep } = useRegistrationStore();

	switch (currentStep) {
		case 1:
			return <OnboardingRegistrationAccountDetails />;

		case 2:
			return <OnboardingRegistrationContactDetails />;

		default:
			return null;
	}
});
